import React, { FC, useEffect, useState } from 'react';
import { InputAdornment, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useDebouncedValue } from './utils';

interface GlobalFilterProps {
  value: string;
  onChange: (value: string) => void;
  /** Floating label. Omitted by default; the search is placeholder-only. */
  label?: string;
  placeholder?: string;
  debounce?: number;
}

/** Debounced free-text search bound to the table's `globalFilter` state. */
const GlobalFilter: FC<GlobalFilterProps> = ({
  value,
  onChange,
  label,
  placeholder = 'Search…',
  debounce = 300,
}: GlobalFilterProps) => {
  const [local, setLocal] = useState(value);
  const debounced = useDebouncedValue(local, debounce);

  useEffect(() => {
    setLocal(value);
  }, [value]);

  useEffect(() => {
    if (debounced !== value) {
      onChange(debounced);
    }
    // Intentionally only react to the debounced value changing.
  }, [debounced]);

  return (
    <TextField
      size="small"
      label={label}
      value={local}
      placeholder={placeholder}
      onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setLocal(e.target.value)}
      inputProps={{ 'aria-label': label ? undefined : placeholder }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
      }}
    />
  );
};

export default GlobalFilter;
